export type RuntimeValue = string | number | boolean | null | undefined | object;

export type ExtendedRuntimeValue = RuntimeValue | bigint | symbol;

export type ObjectRecordValue = ExtendedRuntimeValue;

export type ObjectRecord = Record<string, ObjectRecordValue>;

export function isObjectRecord(value: unknown): value is ObjectRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

type PrimitiveTypeMap = {
  string: string;
  number: number;
  boolean: boolean;
};

/**
 * Read a typed property off an `unknown` value. Returns `undefined` when
 * `value` is not an object-record or the property is the wrong runtime type.
 * So diagnostics, http-client and similar boundary code share one safe-property reader.
 */
function getTypedProperty<K extends keyof PrimitiveTypeMap>(
  value: unknown,
  key: string,
  expected: K,
): PrimitiveTypeMap[K] | undefined {
  if (!isObjectRecord(value)) {
    return undefined;
  }
  const propertyValue = value[key];
  return typeof propertyValue === expected ? (propertyValue as PrimitiveTypeMap[K]) : undefined;
}

export function getStringProperty(value: unknown, key: string): string | undefined {
  return getTypedProperty(value, key, 'string');
}

export function getNumberProperty(value: unknown, key: string): number | undefined {
  return getTypedProperty(value, key, 'number');
}

export function getBooleanProperty(value: unknown, key: string): boolean | undefined {
  return getTypedProperty(value, key, 'boolean');
}

export function isDefined<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined;
}

export function isNonNull<T>(value: T | null): value is T {
  return value !== null;
}
